import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Plus, Trash2, IndianRupee, CreditCard, QrCode, Loader2, Wallet,
  ArrowUpRight, Building2, Smartphone, Upload
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WalletRedemptionSection({ userType, token, walletBalance = 0, onRedemptionSuccess }) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [redemptionRequests, setRedemptionRequests] = useState([]);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [methodForm, setMethodForm] = useState({ method_type: 'upi', upi_id: '', account_holder_name: '', account_number: '', ifsc_code: '', bank_name: '', is_primary: false });
  const [redeemForm, setRedeemForm] = useState({ amount: '', payment_method_id: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [qrFile, setQrFile] = useState(null);
  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/wallet-redemption/payment-methods`, { headers: getHeaders() });
      setPaymentMethods(res.data.methods || []);
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchRedemptionRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/wallet-redemption/my-requests`, { headers: getHeaders() });
      setRedemptionRequests(res.data.requests || []);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => {
    fetchPaymentMethods();
    fetchRedemptionRequests();
  }, [fetchPaymentMethods, fetchRedemptionRequests]);

  const handleAddMethod = async () => {
    if (methodForm.method_type === 'upi' && !methodForm.upi_id) {
      toast.error('Enter UPI ID');
      return;
    }
    if (methodForm.method_type === 'bank_account' && (!methodForm.account_number || !methodForm.ifsc_code)) {
      toast.error('Enter account number and IFSC code');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('user_type', userType);
      formData.append('method_type', methodForm.method_type);
      formData.append('upi_id', methodForm.upi_id);
      formData.append('account_holder_name', methodForm.account_holder_name);
      formData.append('account_number', methodForm.account_number);
      formData.append('ifsc_code', methodForm.ifsc_code);
      formData.append('bank_name', methodForm.bank_name);
      formData.append('is_primary', methodForm.is_primary);
      if (qrFile) formData.append('qr_image', qrFile);

      await axios.post(`${API}/api/wallet-redemption/payment-methods`, formData, { headers: getHeaders() });
      toast.success('Payment method added');
      setShowAddMethod(false);
      setMethodForm({ method_type: 'upi', upi_id: '', account_holder_name: '', account_number: '', ifsc_code: '', bank_name: '', is_primary: false });
      setQrFile(null);
      fetchPaymentMethods();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add payment method');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMethod = async (methodId) => {
    try {
      await axios.delete(`${API}/api/wallet-redemption/payment-methods/${methodId}`, { headers: getHeaders() });
      toast.success('Payment method removed');
      fetchPaymentMethods();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleRedeem = async () => {
    if (!redeemForm.amount || parseFloat(redeemForm.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!redeemForm.payment_method_id) {
      toast.error('Select a payment method');
      return;
    }
    if (parseFloat(redeemForm.amount) > walletBalance) {
      toast.error(`Insufficient balance. Available: ₹${walletBalance}`);
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', redeemForm.amount);
      formData.append('payment_method_id', redeemForm.payment_method_id);
      formData.append('notes', redeemForm.notes);
      await axios.post(`${API}/api/wallet-redemption/redeem`, formData, { headers: getHeaders() });
      toast.success('Redemption request submitted to admin');
      setShowRedeemDialog(false);
      setRedeemForm({ amount: '', payment_method_id: '', notes: '' });
      fetchRedemptionRequests();
      if (onRedemptionSuccess) onRedemptionSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const getMethodLabel = (m) => {
    if (m.method_type === 'upi') return `UPI: ${m.upi_id}`;
    if (m.method_type === 'bank_account') return `${m.bank_name || 'Bank'}: ****${(m.account_number || '').slice(-4)}`;
    if (m.method_type === 'qr_code') return 'QR Code';
    return m.method_type;
  };

  const getMethodIcon = (type) => {
    if (type === 'upi') return <Smartphone className="w-4 h-4 text-violet-500" />;
    if (type === 'bank_account') return <Building2 className="w-4 h-4 text-blue-500" />;
    return <QrCode className="w-4 h-4 text-emerald-500" />;
  };

  return (
    <div className="space-y-4" data-testid="wallet-redemption-section">
      {/* Payment Methods */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Payment Methods</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setShowAddMethod(true)} data-testid="add-payment-method-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Method
          </Button>
        </CardHeader>
        <CardContent>
          {paymentMethods.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No payment methods added. Add a bank account, UPI ID, or QR code to enable redemption.</p>
          ) : (
            <div className="space-y-2">
              {paymentMethods.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-3 border rounded-lg bg-white" data-testid="payment-method-row">
                  <div className="flex items-center gap-3">
                    {getMethodIcon(m.method_type)}
                    <div>
                      <p className="text-sm font-medium">{getMethodLabel(m)}</p>
                      {m.account_holder_name && <p className="text-xs text-slate-400">{m.account_holder_name}</p>}
                    </div>
                    {m.is_primary && <Badge className="bg-teal-100 text-teal-700 text-[10px]">Primary</Badge>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteMethod(m.id)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Redeem Button */}
      <Button onClick={() => setShowRedeemDialog(true)} className="w-full" disabled={paymentMethods.length === 0} data-testid="redeem-wallet-btn">
        <Wallet className="w-4 h-4 mr-2" /> Redeem Wallet Amount
      </Button>

      {/* Redemption History */}
      {redemptionRequests.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Redemption Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {redemptionRequests.map((req) => (
                <div key={req.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid="redemption-request-row">
                  <div>
                    <p className="text-sm font-medium">₹{req.amount?.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-slate-400">
                      {req.payment_method?.method_type === 'upi' ? `UPI: ${req.payment_method?.upi_id}` :
                       req.payment_method?.method_type === 'bank_account' ? `Bank: ****${(req.payment_method?.account_number || '').slice(-4)}` : 'QR Code'}
                    </p>
                    <p className="text-xs text-slate-400">{req.created_at ? new Date(req.created_at).toLocaleDateString('en-IN') : ''}</p>
                  </div>
                  <Badge className={
                    req.status === 'approved' ? 'bg-green-100 text-green-700' :
                    req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }>{req.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Payment Method Dialog */}
      <Dialog open={showAddMethod} onOpenChange={setShowAddMethod}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={methodForm.method_type} onValueChange={(v) => setMethodForm(f => ({ ...f, method_type: v }))}>
                <SelectTrigger data-testid="payment-method-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upi">UPI ID</SelectItem>
                  <SelectItem value="bank_account">Bank Account</SelectItem>
                  <SelectItem value="qr_code">QR Code</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account Holder Name</Label>
              <Input value={methodForm.account_holder_name} onChange={(e) => setMethodForm(f => ({ ...f, account_holder_name: e.target.value }))} placeholder="Name" />
            </div>
            {methodForm.method_type === 'upi' && (
              <div>
                <Label>UPI ID</Label>
                <Input value={methodForm.upi_id} onChange={(e) => setMethodForm(f => ({ ...f, upi_id: e.target.value }))} placeholder="name@upi" data-testid="upi-id-input" />
              </div>
            )}
            {methodForm.method_type === 'bank_account' && (
              <>
                <div>
                  <Label>Bank Name</Label>
                  <Input value={methodForm.bank_name} onChange={(e) => setMethodForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="e.g. HDFC Bank" />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input value={methodForm.account_number} onChange={(e) => setMethodForm(f => ({ ...f, account_number: e.target.value }))} placeholder="Account number" data-testid="bank-account-input" />
                </div>
                <div>
                  <Label>IFSC Code</Label>
                  <Input value={methodForm.ifsc_code} onChange={(e) => setMethodForm(f => ({ ...f, ifsc_code: e.target.value }))} placeholder="e.g. HDFC0001234" />
                </div>
              </>
            )}
            {methodForm.method_type === 'qr_code' && (
              <div>
                <Label>Upload QR Code Image</Label>
                <Input type="file" accept="image/*" onChange={(e) => setQrFile(e.target.files[0])} data-testid="qr-upload-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMethod(false)}>Cancel</Button>
            <Button onClick={handleAddMethod} disabled={submitting} data-testid="save-payment-method-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem Dialog */}
      <Dialog open={showRedeemDialog} onOpenChange={setShowRedeemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Redeem Wallet Amount</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-teal-50 rounded-lg text-center">
              <p className="text-xs text-teal-600">Available Balance</p>
              <p className="text-2xl font-bold text-teal-700">₹{walletBalance.toLocaleString('en-IN')}</p>
            </div>
            <div>
              <Label>Amount to Redeem (₹)</Label>
              <Input type="number" value={redeemForm.amount} onChange={(e) => setRedeemForm(f => ({ ...f, amount: e.target.value }))} placeholder="Enter amount" data-testid="redeem-amount-input" />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={redeemForm.payment_method_id} onValueChange={(v) => setRedeemForm(f => ({ ...f, payment_method_id: v }))}>
                <SelectTrigger data-testid="redeem-method-select">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{getMethodLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={redeemForm.notes} onChange={(e) => setRedeemForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any notes for admin" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeemDialog(false)}>Cancel</Button>
            <Button onClick={handleRedeem} disabled={submitting} data-testid="submit-redeem-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
